/* eslint-disable */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Page from '../components/Page';
import Iconify from '../components/Iconify';
import { authApiCall } from '../services/http';
import { endpoints } from '../config';
import { setAuthToken, setAuthUser } from '../redux/reducers/authSlice';
import copyToClipboard from '../utils/copyToClipboard';
import SubscriptionsKpis from './subscriptions/components/SubscriptionsKpis';
import ForecastChart from './subscriptions/components/ForecastChart';
import CancellationsTable from './subscriptions/components/CancellationsTable';
import AnomaliesSection from './subscriptions/components/AnomaliesSection';
import OneTimePaymentsSection from './subscriptions/components/OneTimePaymentsSection';
import SubscriptionsListTable from './subscriptions/components/SubscriptionsListTable';
import ProductsManagement from './subscriptions/components/ProductsManagement';

export default function Subscriptions() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.auth.authToken);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [productsOpen, setProductsOpen] = useState(false);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authApiCall({
        method: 'GET',
        endpoint: endpoints.stripeStats,
        path: '',
        navigate,
        dispatch,
        setAuthUser,
        setAuthToken,
        authToken,
        redirectRoute: null,
      });
      setStats(res?.data ?? null);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Error cargando métricas');
    } finally {
      setLoading(false);
    }
  }, [authToken, dispatch, navigate]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const runSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await authApiCall({
        method: 'POST',
        endpoint: endpoints.stripeSync,
        path: '',
        navigate,
        dispatch,
        setAuthUser,
        setAuthToken,
        authToken,
        redirectRoute: null,
      });
      setSyncResult({ ok: true, data: res?.data ?? {} });
      // Refrescar métricas en background — el Dialog se queda mostrando el desglose.
      loadStats();
    } catch (e) {
      setSyncResult({ ok: false, error: e?.response?.data?.error || e?.message || 'Error al sincronizar' });
    } finally {
      setSyncing(false);
    }
  };

  const closeSyncDialog = () => {
    if (syncing) return;
    setSyncDialogOpen(false);
    setSyncResult(null);
  };

  // Lookup stripeSubscriptionId → datos económicos para que SelectionMetrics pueda
  // calcular MRR/ARR de subs Stripe (el endpoint /list no expone tarifa).
  // - mensuales: stats.debug.monthlyContributions[].amountEUR ya es la contribución MRR
  //   y a la vez el ciclo (la cuota mensual coincide con el MRR).
  // - anuales: stats.renewalsByMonth[].items[].cycleAmountEUR es el importe del ciclo (×1/año).
  // - one_time: stats.oneTimePayments[] no aporta MRR pero exponemos el importe como ciclo
  //   informativo para el desplegable de detalle.
  const stripeStatsLookup = useMemo(() => {
    const map = new Map();
    if (!stats) return map;
    (stats.debug?.monthlyContributions || []).forEach((c) => {
      if (!c?.id) return;
      const amount = Number(c.amountEUR) || 0;
      map.set(c.id, {
        mrrEur: amount,
        cycleAmountEur: amount,
        billingInterval: 'month',
        qty: Number(c.qty) || null,
      });
    });
    (stats.renewalsByMonth || []).forEach((m) => {
      (m?.items || []).forEach((it) => {
        if (!it?.id) return;
        map.set(it.id, {
          mrrEur: (Number(it.cycleAmountEUR) || 0) / 12,
          cycleAmountEur: Number(it.cycleAmountEUR) || 0,
          billingInterval: 'year',
          qty: Number(it.qty) || null,
        });
      });
    });
    (stats.oneTimePayments || []).forEach((p) => {
      if (!p?.id) return;
      const cycleAmountEur = Number(p.amountEUR ?? p.cycleAmountEUR) || 0;
      map.set(p.id, {
        mrrEur: 0,
        cycleAmountEur,
        billingInterval: 'one_time',
        qty: Number(p.qty) || null,
      });
    });
    return map;
  }, [stats]);

  return (
    <Page title="Suscripciones">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5} flexWrap="wrap" gap={2}>
          <Typography variant="h4" gutterBottom>
            Suscripciones
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Iconify icon="eva:settings-2-outline" />}
              onClick={() => setProductsOpen(true)}
            >
              Gestionar planes
            </Button>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:sync-outline" />}
              onClick={() => setSyncDialogOpen(true)}
              disabled={loading || syncing}
              sx={{
                bgcolor: '#635BFF',
                color: '#fff',
                '&:hover': { bgcolor: '#0A2540' },
              }}
            >
              Sincronizar Stripe
            </Button>
          </Stack>
        </Stack>

        {loading && !stats && (
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress size={64} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {stats && (
          <>
            <Box mb={3}>
              <SubscriptionsKpis stats={stats} />
            </Box>

            <Box mb={3}>
              <ForecastChart stats={stats} />
            </Box>
          </>
        )}

        <Box mb={3}>
          <SubscriptionsListTable stripeStatsLookup={stripeStatsLookup} stats={stats} />
        </Box>

        {stats && (
          <>
            <Box mb={3}>
              <CancellationsTable renewals={stats.canceledRenewals} monthly={stats.canceledMonthly} />
            </Box>

            <Box mb={3}>
              <OneTimePaymentsSection
                items={stats.oneTimePayments}
                total={stats.oneTimePaymentsTotal}
                activeCount={stats.activeOneTimeSubscriptions}
              />
            </Box>

            <Box mb={3}>
              <AnomaliesSection items={stats.nonActiveSubscriptions} />
            </Box>
          </>
        )}
      </Container>

      <ProductsManagement open={productsOpen} onClose={() => setProductsOpen(false)} />

      <Dialog open={syncDialogOpen} onClose={closeSyncDialog} maxWidth="sm" fullWidth>
        {syncResult?.ok ? (
          <SyncSuccessView data={syncResult.data} onClose={closeSyncDialog} />
        ) : syncResult && !syncResult.ok ? (
          <SyncErrorView error={syncResult.error} onClose={closeSyncDialog} onRetry={runSync} syncing={syncing} />
        ) : (
          <>
            <DialogTitle>Sincronizar con Stripe</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Esto sincroniza la base de datos con el estado actual de Stripe.{' '}
                <strong>Puede modificar suscripciones existentes y enviar emails a usuarios</strong>{' '}
                (p. ej. impagos, cancelaciones). No es reversible. ¿Confirmas?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeSyncDialog} disabled={syncing}>
                Cancelar
              </Button>
              <LoadingButton onClick={runSync} variant="contained" color="warning" loading={syncing}>
                Sincronizar
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Page>
  );
}

// ----------------------------------------------------------------------

function SyncSuccessView({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(data, null, 2);

  const handleCopy = async () => {
    await copyToClipboard(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify icon="eva:checkmark-circle-2-fill" width={24} sx={{ color: 'success.main' }} />
          <span>Sincronización completada</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative' }}>
          <Tooltip title={copied ? 'Copiado' : 'Copiar JSON'}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'background.paper' },
              }}
            >
              <Iconify icon={copied ? 'eva:checkmark-fill' : 'eva:copy-outline'} width={16} height={16} />
            </IconButton>
          </Tooltip>
          <Box
            component="pre"
            sx={{
              bgcolor: 'grey.100',
              color: 'text.primary',
              p: 2,
              m: 0,
              borderRadius: 1,
              fontSize: 12,
              fontFamily: 'monospace',
              overflow: 'auto',
              maxHeight: 480,
              whiteSpace: 'pre',
            }}
          >
            {json}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Cerrar
        </Button>
      </DialogActions>
    </>
  );
}

function SyncErrorView({ error, onClose, onRetry, syncing }) {
  return (
    <>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify icon="eva:alert-circle-fill" width={24} sx={{ color: 'error.main' }} />
          <span>Error en la sincronización</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={syncing}>
          Cerrar
        </Button>
        <LoadingButton onClick={onRetry} variant="contained" color="warning" loading={syncing}>
          Reintentar
        </LoadingButton>
      </DialogActions>
    </>
  );
}
